import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import FeedCard from "@/components/feed/FeedCard";
import StoryStrip from "@/components/feed/StoryStrip";
import { Loader2 } from "lucide-react";

/** stilltouchedX-Feed (P4). Eigener Feed des Ü18-Bereichs.
 *  Phase 3: einheitliche Composer+Stories-Reihe (Erstellen-Kachel öffnet den STX-Composer),
 *  Stories strikt über STX-Ablage + stx_profiles-Identität getrennt (kein Social-Leak). */
export default function StxFeed() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [storyGroups, setStoryGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const _codes = (user?.capabilities || []).map((c) => c?.capability_code || c);
  const canCreate = !!user && user !== false && (
    ["ADMIN", "owner"].includes(user?.role) || user?.is_admin ||
    _codes.includes("ST_CREATOR") || user?.st_creator === true
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [feedRes, storyRes] = await Promise.all([
        api.get("/stx/feed?limit=30"),
        api.get("/stx/stories/entries").catch(() => ({ data: { story_groups: [] } })),
      ]);
      setPosts(feedRes.data.posts || []);
      setStoryGroups(storyRes.data.story_groups || []);
    } catch {
      setPosts([]); setStoryGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onPublished = () => load();
    window.addEventListener("feed-post-published", onPublished);
    window.addEventListener("feed-refresh", onPublished);
    return () => {
      window.removeEventListener("feed-post-published", onPublished);
      window.removeEventListener("feed-refresh", onPublished);
    };
  }, [load]);

  return (
    <div data-testid="stx-feed" className="w-full">
      {/* Phase 3 · Erstellen-Kachel (öffnet STX-Composer) + STX-Stories in einer Reihe */}
      <StoryStrip
        storyGroups={storyGroups}
        loading={loading}
        canCreate={canCreate}
        area="STX"
        onCreate={() => window.dispatchEvent(new CustomEvent("open-feed-composer", { detail: { area: "STILLTOUCHEDX" } }))}
      />

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-stx-accent" size={24} /></div>
      ) : posts.length === 0 ? (
        <p data-testid="stx-feed-empty" className="text-center text-sm text-stx-text-muted py-12">
          {t("stx.feed_empty", { defaultValue: "Noch keine Beiträge im stilltouchedX-Feed." })}
        </p>
      ) : (
        <div className="py-2">
          {posts.map((p) => <FeedCard key={p.post_id} post={p} />)}
        </div>
      )}
    </div>
  );
}
